import { useDisclosure } from "@heroui/modal";
import { useCurrentAccount } from "@mysten/dapp-kit-react";
import { useMemo } from "react";

import { AppModal } from "@/components/ui/AppModal";
import { useAuthenticationContext } from "@/context/authentication/useAuthenticationContext";
import { useClaimsContext } from "@/context/claims/useClaimsContext";
import { useSignPersonalMessage } from "@/hooks/dappkit";
import {
  formatBaseUnits,
  formatBaseUnitsForDisplay,
  toBaseUnitsFloor,
} from "@/lib/currency/tokenAmount";
import { Button } from "@/ui/nextui/Button/Button";
import { useToast } from "@/ui/shadcn/use-toast";

import { ClaimLeasingRewardsProps } from "./ClaimLeasingRewards.types";

/** Minimum claim, in base units. Mirrors the same rule on the API route. */
const MIN_CLAIM_UNITS = toBaseUnitsFloor(10)!;

export const ClaimLeasingRewards: React.FC<ClaimLeasingRewardsProps> = ({
  leaseId,
  calculateLeasingRewardsResponse,
}) => {
  const { isOpen, onOpen, onClose, onOpenChange } = useDisclosure();

  const account = useCurrentAccount();
  const { claimLeasingRewards } = useClaimsContext();
  const { mutate: signPersonalMessage } = useSignPersonalMessage();
  const { generateNonce } = useAuthenticationContext();
  const { toast } = useToast();

  /**
   * The claim amount, in base units.
   *
   * A lease can only be claimed once, so the API requires the *full* unlocked
   * balance — there is nothing for the user to choose here, which is why this
   * modal has no amount input. The amount is therefore derived, not form state.
   *
   * `unlockedRewards` is a float built by summing products, so it usually carries
   * more than the 8 decimals $TRUTH can represent. It has to be floored: the API
   * rejects anything above its own balance, so rounding up fails, and it rejects
   * anything more than one unit below it, so truncating further fails too.
   *
   * This is what used to make the lease unclaimable: the amount was set to
   * `unlockedRewards.toString()` (e.g. "17700874.899340399"), and the submit
   * button disabled itself on any value with more than 8 decimals.
   */
  const claimUnits = useMemo(
    () => toBaseUnitsFloor(calculateLeasingRewardsResponse?.unlockedRewards ?? 0) ?? 0n,
    [calculateLeasingRewardsResponse?.unlockedRewards],
  );

  const claimAmountString = formatBaseUnits(claimUnits);
  const claimAmount = Number(claimAmountString);

  const isClaimingDisabled = !calculateLeasingRewardsResponse?.isClaimingEnabled;

  // Single source of truth for why a claim can't go through, shared by the
  // disabled state and the submit guard.
  const claimError = useMemo(() => {
    if (claimUnits <= 0n) return "There are no unlocked rewards to claim yet.";

    if (claimUnits < MIN_CLAIM_UNITS) {
      return `Minimum claim amount is ${formatBaseUnits(MIN_CLAIM_UNITS)} $TRUTH.`;
    }

    // The claim batcher's per-claim ceiling. A lease cannot be split, so above
    // it there is no smaller amount to offer — the API refuses the claim, and
    // without this the user would only find out after signing.
    const ceiling = calculateLeasingRewardsResponse?.maxClaimUnits;

    if (ceiling && claimUnits > BigInt(ceiling)) {
      return `This lease's rewards exceed the maximum of ${formatBaseUnits(BigInt(ceiling))} $TRUTH per claim. Please contact support.`;
    }

    return null;
  }, [claimUnits, calculateLeasingRewardsResponse?.maxClaimUnits]);

  function onSubmit() {
    if (claimError) {
      toast({
        title: "Error",
        description: claimError,
        variant: "destructive",
      });

      return;
    }

    // The season score warning modal that could sit in between was removed with
    // the seasons (2026-09-27), so a valid claim goes straight to signing.
    onPressClaimLeasingRewards();
  }

  async function onPressClaimLeasingRewards() {
    if (!account?.address) return;

    try {
      // Step 1: Generate nonce
      const nonceData = await generateNonce(account.address);

      if (!nonceData) {
        toast({
          title: "Error",
          description: "Failed to generate security nonce. Please try again.",
          variant: "destructive",
        });

        return;
      }

      // Step 2: Create message with nonce and timestamp.
      // The lease and amount are part of the signed text so the server can bind the
      // signature to this specific claim rather than to the nonce alone. Both the
      // signed text and the body come from the same canonical decimal string, so
      // the server's signed-amount comparison is exact.
      const message = new TextEncoder().encode(
        `I confirm that I'm claiming $TRUTH tokens to be sent to my connected wallet. lease_id: ${leaseId} amount: ${claimAmountString} nonce: ${nonceData.nonce}`,
      );

      // Step 3: Sign the message
      signPersonalMessage(
        {
          message,
        },
        {
          onSuccess(data) {
            claimLeasingRewards({
              user_address: account.address,
              lease_id: leaseId,
              message: Array.from(message),
              signature: data.signature,
              nonce: nonceData.nonce,
              nonceToken: nonceData.nonceToken,
              amount: claimAmount,
            });

            onClose(); // Close modal after successful submission
          },
          onError(error) {
            toast({
              title: "Error",
              description: <p className="mb-0">While submitting your signature: {error.message}</p>,
              variant: "destructive",
            });
          },
        },
      );
    } catch {
      toast({
        title: "Error",
        description: "Failed to process lease request. Please try again.",
        variant: "destructive",
      });
    }
  }

  // Check if we're still loading data
  const isLoading = !calculateLeasingRewardsResponse;

  return (
    <>
      {isLoading ? (
        <div
          className="h-[44px] w-full animate-pulse rounded-none bg-gray-700"
          id={`claim-leasing-rewards-${leaseId}-button-skeleton`}
        />
      ) : (
        <Button
          fullWidth
          className="rounded-none"
          color={isClaimingDisabled ? "default" : "success"}
          id={`claim-leasing-rewards-${leaseId}-open-button`}
          size="lg"
          type="button"
          onPress={onOpen}
        >
          Claim Your Rewards
        </Button>
      )}

      <AppModal
        footer={
          <>
            <Button
              color="danger"
              id={`claim-leasing-rewards-${leaseId}-cancel-button`}
              variant="light"
              onPress={onClose}
            >
              Cancel
            </Button>
            <Button
              color="success"
              id={`claim-leasing-rewards-${leaseId}-submit-button`}
              isDisabled={!!claimError}
              type="button"
              onPress={onSubmit}
            >
              <>Claim {formatBaseUnitsForDisplay(claimUnits)} $TRUTH</>
            </Button>
          </>
        }
        id={`claim-leasing-rewards-${leaseId}-modal`}
        isOpen={isOpen}
        size="md"
        subtitle="Review and confirm your reward claim"
        title="Confirm Claim"
        onClose={onClose}
        onOpenChange={onOpenChange}
      >
        {/* Early Claim Penalty Warning */}
        {!!calculateLeasingRewardsResponse?.burnPenalty &&
          calculateLeasingRewardsResponse.burnPenalty > 0 && (
            <div
              className="mb-4 rounded-sm border border-yellow-500/30 bg-yellow-500/5 p-3"
              id={`claim-leasing-rewards-${leaseId}-burn-penalty-notice`}
            >
              <p
                className="mb-1 text-[10px] uppercase tracking-widest text-yellow-400"
                id={`claim-leasing-rewards-${leaseId}-burn-penalty-title`}
              >
                Early Claim Penalty
              </p>
              <p
                className="mb-0 text-xs text-gray-400"
                id={`claim-leasing-rewards-${leaseId}-burn-penalty-text`}
              >
                {calculateLeasingRewardsResponse?.burnPenalty}% applies to claims until day{" "}
                {calculateLeasingRewardsResponse?.burnPenalty === 50
                  ? 31
                  : calculateLeasingRewardsResponse?.burnPenalty === 30
                    ? 93
                    : 0}
                .
              </p>
            </div>
          )}

        {/* Claim Amount Display */}
        <div
          className="border-b border-gray-800 pb-4 text-center"
          id={`claim-leasing-rewards-${leaseId}-available`}
        >
          <p
            className="mb-1 text-sm font-bold uppercase tracking-widest text-green-500"
            id={`claim-leasing-rewards-${leaseId}-available-label`}
          >
            Available to Claim
          </p>
          <h2
            className="mb-2 text-4xl font-bold text-white"
            id={`claim-leasing-rewards-${leaseId}-available-value`}
          >
            {/* Full 8-decimal precision, truncated rather than rounded: this is
                the exact amount that will be requested and signed, so a 2-decimal
                headline was quoting a different number than the claim itself. */}
            {formatBaseUnitsForDisplay(claimUnits)}
          </h2>
          <p
            className="text-sm text-gray-400"
            id={`claim-leasing-rewards-${leaseId}-available-ticker`}
          >
            $TRUTH
          </p>
        </div>

        {/* Additional Info */}
        <div className="pt-4 text-center" id={`claim-leasing-rewards-${leaseId}-footnote`}>
          {claimError ? (
            <p
              className="mb-0 text-xs text-yellow-400"
              id={`claim-leasing-rewards-${leaseId}-error-note`}
            >
              {claimError}
            </p>
          ) : (
            <p
              className="mb-0 text-xs text-gray-400"
              id={`claim-leasing-rewards-${leaseId}-full-claim-note`}
            >
              A lease is always claimed in full — a partial claim would forfeit the remainder.
            </p>
          )}
        </div>
      </AppModal>
    </>
  );
};
